/**
 * Persistent progress — GAM-10, GAM-11, GAM-12
 */

export const PROGRESS_STORAGE_KEY = 'glifo-game-progress';

const CURRENT_VERSION = 1;

/**
 * @typedef {Object} Progress
 * @property {number} version
 * @property {Record<string, { stars: number, bestTimeMs?: number }>} levels
 * @property {{ lastPlayedDate?: string, streak?: number }} [daily]
 * @property {boolean} [streakShield]
 * @property {Record<string, unknown>} [settings]
 */

/**
 * @returns {Progress}
 */
export function defaultProgress() {
  return {
    version: CURRENT_VERSION,
    levels: {},
    daily: {},
    streakShield: false,
    settings: {
      audio: true,
      colorblind: false,
      premium: false,
    },
  };
}

/**
 * @param {unknown} raw
 * @returns {Progress}
 */
export function migrateProgress(raw) {
  if (raw === null || typeof raw !== 'object') return defaultProgress();

  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    version: CURRENT_VERSION,
    levels:
      typeof o.levels === 'object' && o.levels !== null
        ? /** @type {Record<string, { stars: number, bestTimeMs?: number }>} */ ({
            .../** @type {object} */ (o.levels),
          })
        : {},
    daily:
      typeof o.daily === 'object' && o.daily !== null
        ? { .../** @type {object} */ (o.daily) }
        : {},
    streakShield: typeof o.streakShield === 'boolean' ? o.streakShield : false,
    settings:
      typeof o.settings === 'object' && o.settings !== null
        ? { .../** @type {object} */ (o.settings) }
        : {},
  };
}

/**
 * @param {Progress} p
 * @param {string} levelId
 * @param {{ stars: number, bestTimeMs?: number }} row
 * @returns {Progress}
 */
export function upsertLevelProgress(p, levelId, row) {
  return {
    ...p,
    levels: { ...p.levels, [levelId]: row },
  };
}

/**
 * Simple storage facade — GAM-11 Safari private / quota failures.
 * @param {{ getItem:(k:string)=>string|null, setItem:(k:string,v:string)=>void }} backend
 * @param {{ onWarning?: () => void }} [hooks]
 */
export function createProgressAdapter(backend, hooks = {}) {
  let memoryMode = false;
  /** @type {Progress | null} */
  let memorySnapshot = null;

  function read() {
    if (memoryMode && memorySnapshot) return migrateProgress(memorySnapshot);
    try {
      const s = backend.getItem(PROGRESS_STORAGE_KEY);
      return migrateProgress(s ? JSON.parse(s) : null);
    } catch {
      memoryMode = true;
      hooks.onWarning?.();
      return migrateProgress(memorySnapshot);
    }
  }

  function write(p) {
    const json = JSON.stringify(p);
    if (memoryMode) {
      memorySnapshot = JSON.parse(json);
      return;
    }
    try {
      backend.setItem(PROGRESS_STORAGE_KEY, json);
    } catch {
      memoryMode = true;
      hooks.onWarning?.();
      memorySnapshot = JSON.parse(json);
    }
  }

  return {
    /** @returns {Progress} */
    load() {
      return read();
    },
    /** @param {Progress} p */
    save(p) {
      write(p);
    },
    get memoryMode() {
      return memoryMode;
    },
  };
}

/**
 * In-memory backend for tests / Node.
 */
export function createMemoryBackend() {
  /** @type {Map<string, string>} */
  const m = new Map();
  return {
    getItem(k) {
      return m.has(k) ? m.get(k) : null;
    },
    setItem(k, v) {
      m.set(k, v);
    },
  };
}
